import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';

import SignUpRouter from './Auth/SignUpRouter';
import Login from './Auth/Login';
import routes from '../constants/routes';

import KnockKnockInfoForm from './Auth/screens/KnockKnockInfoForm';
import useFeatureFlagContext from '../providers/useFeatureFlagContext';
import constants from '../constants/index';
import legacyRouteMap from '../constants/webRouteMap';
import QmImage from '../components/Image/QmImage';
import { clearAppContextCookie, clearJWTCookie } from '../util/cookie';

const HOMEOWNER_APP_URL = constants.WORKING_CONFIG.HOMEOWNER_APP_URL;

const Router = () => {
  const featureFlags = useFeatureFlagContext();
  const [initialRoute] = useState(window.location.toString());

  // isRedirecting is going to be used to load in a placeholder while the browser redirect kicks in
  // waiting on design here
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(
    () => {
      if (!isEmpty(featureFlags) && initialRoute) {
        Object.entries(legacyRouteMap).forEach(([routeKey, route]) => {
          const routeRegex = new RegExp(
            routeKey
              .replace(/:id/, '(?<id>\\d{1,})')
              .replace(/:token/, '(?<token>[a-zA-Z0-9-_]+)'),
            'g',
          );

          const match = routeRegex.exec(initialRoute);

          // if the route key matches the current url and the feature flag is enabled, redirect to the new route
          if (match && match.length && featureFlags[route.featureFlagKey]) {
            let newRoute = route.newRoute;

            // replace id and token in string if necessary
            if (match.groups && match.groups.id) {
              newRoute = newRoute.replace(':id', match.groups.id);
            }

            if (match.groups && match.groups.token) {
              newRoute = newRoute.replace(':token', match.groups.token);
            }

            let url = `${HOMEOWNER_APP_URL}${newRoute}`;

            // forward any query params
            if (window.location.search) {
              url += window.location.search;
            }

            // we send display actions as anchor elements
            if (window.location.hash) {
              url += window.location.hash;
            }

            setIsRedirecting(true);

            if (initialRoute.includes('fresh')) {
              // retain any admin app login state
              const qmAdminAuthData = window.localStorage.getItem('qm_aa.auth');
              const qmAdminToken = window.localStorage.getItem('qm_aa.token');
              const qmSavedState = window.localStorage.getItem(
                'qm_aa.saved_states',
              );

              window.localStorage.clear();

              if (qmAdminAuthData) {
                window.localStorage.setItem('qm_aa.auth', qmAdminAuthData);
              }
              if (qmAdminToken) {
                window.localStorage.setItem('qm_aa.token', qmAdminToken);
              }
              if (qmSavedState) {
                window.localStorage.setItem('qm_aa.saved_states', qmSavedState);
              }

              // try to clear new web app cookie if it exists
              clearJWTCookie();
              clearAppContextCookie();
            }
            window.location.replace(url);
          }
        });
      }

      return () => {
        if (isRedirecting) {
          setIsRedirecting(false);
        }
      };
    },
    [featureFlags, initialRoute],
  );

  if (isEmpty(featureFlags)) {
    return <div>Loading...</div>;
  }

  if (isRedirecting) {
    return (
      <div>
        <QmImage
          src="static/app-redirection.png"
          height={400}
          width={400}
          contain
        />
      </div>
    );
  }

  return (
    <BrowserRouter basename="/app">
      <Switch>
        <Route path="/signup/knockknock" component={KnockKnockInfoForm} />
        <Route
          path="/join/invitation/outreach/:id/:token"
          render={props => (
            <Redirect
              to={{
                pathname: '/signup/outreach',
                state: {
                  location: props.location,
                  match: props.match,
                  fresh: true,
                },
              }}
            />
          )}
        />
        <Route
          path="/join/invitation/fresh/:id/:token"
          render={props => {
            const queryParams = new URLSearchParams(window.location.search);
            const address = queryParams.get('address');
            return (
              <Redirect
                to={{
                  pathname: '/signup',
                  state: {
                    location: props.location,
                    match: props.match,
                    fresh: true,
                    address_line1: address,
                  },
                  search: !!featureFlags['WEB_AUTH_SIGN_UP_INVITATION_PAGE']
                    ? queryParams.toString()
                    : '',
                }}
              />
            );
          }}
        />
        <Route
          path="/join/invitation/:id/:token"
          render={props => (
            <Redirect
              to={{
                pathname: '/signup',
                state: {
                  location: props.location,
                  match: props.match,
                },
              }}
            />
          )}
        />
        <Route path={routes.SIGNUP} component={SignUpRouter} />
        <Route path="/login" component={Login} />
      </Switch>
    </BrowserRouter>
  );
};

export default Router;
